// src/taxonomies/scope-typology-taxonomy.js

/* ============================================================================
   TAXONOMY — SCOPE / TYPOLOGY (Onglet 2 : Typology)
   Objectif : libellés métier, non ambigus, V1 déployable, déterministe pour N1/N2.
   IMPORTANT : cette version assume une taxonomie "définitive" (codes stables).
============================================================================ */

/* --- Classification (Sector → Typology → Sub-typology) -------------------- */
export const SECTOR_OPTIONS = [
  { value: "BUILD", label: "Bâtiment" },
  { value: "INFRA", label: "Infrastructures" },
  { value: "INDUS", label: "Industrie" },
];

/**
 * Typology dépend de Sector
 * Règle : libellés concrets, non ambigus, non redondants avec Sector.
 * NOTE : garder "OTHER_*" pour ne jamais bloquer (template générique côté moteur).
 */
export const TYPOLOGY_OPTIONS = [
  // BUILD
  { parent: "BUILD", value: "BUILD_RESIDENTIAL", label: "Résidentiel" },
  { parent: "BUILD", value: "BUILD_TERTIARY", label: "Tertiaire (bureaux / commerce)" },
  { parent: "BUILD", value: "BUILD_PUBLIC", label: "Équipement public (ERP)" },
  { parent: "BUILD", value: "BUILD_HEALTH", label: "Santé (hôpital / clinique)" },
  //{ parent: "BUILD", value: "BUILD_EDUCATION", label: "Éducation (école / université)" },
  { parent: "BUILD", value: "BUILD_HOSPITALITY", label: "Hôtellerie / tourisme" },
  { parent: "BUILD", value: "BUILD_MIXED", label: "Mixte (plusieurs usages)" },
  { parent: "BUILD", value: "BUILD_OTHER", label: "Autre bâtiment" },

  // INFRA
  { parent: "INFRA", value: "INFRA_WATER_POTABLE", label: "Eau potable (AEP)" },
  { parent: "INFRA", value: "INFRA_SANITATION", label: "Assainissement (collecte / STEP)" },
  { parent: "INFRA", value: "INFRA_ROAD_VRD", label: "Route / VRD (voiries / réseaux secs)" },
  { parent: "INFRA", value: "INFRA_ENERGY", label: "Énergie (poste / lignes / production)" },
  //{ parent: "INFRA", value: "INFRA_TELECOM", label: "Télécom / IT (réseaux / data)" },
  { parent: "INFRA", value: "INFRA_TRANSPORT", label: "Transport (rail / port / aéroport)" },
  //{ parent: "INFRA", value: "INFRA_PUBLIC_REALM", label: "Aménagement urbain / espaces publics" },
  { parent: "INFRA", value: "INFRA_OTHER", label: "Autre infrastructure" },

  // INDUS
  { parent: "INDUS", value: "INDUS_PROCESS_PLANT", label: "Procédé (process plant / unités)" },
  { parent: "INDUS", value: "INDUS_UTILITIES_OFFSITES", label: "Utilités / offsites (énergie / fluides)" },
  { parent: "INDUS", value: "INDUS_STORAGE_DEPOT", label: "Stockage / dépôt (tank farm / entrepôts)" },
  //{ parent: "INDUS", value: "INDUS_RETROFIT_TURNAROUND", label: "Rénovation / retrofit / arrêt technique" },
  { parent: "INDUS", value: "INDUS_OTHER", label: "Autre industrie" },
];

/**
 * Sub-typology dépend de Typology
 * Règle : uniquement des sous-types qui guident réellement la génération N1/N2.
 * Pas de fourre-tout.
 */
export const SUB_TYPOLOGY_OPTIONS = [
  // INFRA — Eau potable (AEP)
  { parent: "INFRA_WATER_POTABLE", value: "AEP_TREATMENT", label: "Station de traitement (WTP)" },
  { parent: "INFRA_WATER_POTABLE", value: "AEP_INTAKE", label: "Captage / prise d’eau / forage" },
  { parent: "INFRA_WATER_POTABLE", value: "AEP_STORAGE", label: "Réservoir / stockage" },
  { parent: "INFRA_WATER_POTABLE", value: "AEP_TRANSMISSION", label: "Adduction / transfert (conduites)" },
  { parent: "INFRA_WATER_POTABLE", value: "AEP_DISTRIBUTION", label: "Réseau de distribution" },

  // INFRA — Assainissement
  { parent: "INFRA_SANITATION", value: "SAN_COLLECTION", label: "Réseau de collecte" },
  { parent: "INFRA_SANITATION", value: "SAN_PUMPING", label: "Poste(s) de pompage" },
  { parent: "INFRA_SANITATION", value: "SAN_WWTP", label: "Station d’épuration (STEP)" },
  { parent: "INFRA_SANITATION", value: "SAN_OUTFALL", label: "Rejet / exutoire" },

  // INFRA — Route / VRD
  { parent: "INFRA_ROAD_VRD", value: "ROAD_URBAN", label: "Voirie urbaine / VRD" },
  { parent: "INFRA_ROAD_VRD", value: "ROAD_HIGHWAY", label: "Route / autoroute" },
  { parent: "INFRA_ROAD_VRD", value: "ROAD_STRUCTURES", label: "Ouvrages (ponts / dalots / murs)" },
  { parent: "INFRA_ROAD_VRD", value: "ROAD_DRY_NETWORKS", label: "Réseaux secs (élec / télécom / éclairage)" },

  // INDUS — Procédé
  { parent: "INDUS_PROCESS_PLANT", value: "INDU_PROCESS_UNIT", label: "Unité de procédé" },
  { parent: "INDUS_PROCESS_PLANT", value: "INDU_PROCESS_LINE", label: "Ligne process / atelier" },
  { parent: "INDUS_PROCESS_PLANT", value: "INDU_SKID_PACKAGE", label: "Skid / package" },

  // INDUS — Utilités / offsites
  { parent: "INDUS_UTILITIES_OFFSITES", value: "INDU_POWER_UTIL", label: "Énergie (HV/LV / production / secours)" },
  { parent: "INDUS_UTILITIES_OFFSITES", value: "INDU_FLUIDS_UTIL", label: "Fluides utilités (air / eau / vapeur)" },
  { parent: "INDUS_UTILITIES_OFFSITES", value: "INDU_CONTROL_UTIL", label: "Contrôle-commande (PLC/SCADA)" },
];

/* --- Morphologie / Périmètre (multi-sélection possible) ------------------- */
export const PROJECT_FOOTPRINT_OPTIONS = [
  { value: "SINGLE_SITE", label: "Site unique" },
  { value: "MULTI_SITE", label: "Multi-sites" },
  { value: "LINEAR", label: "Linéaire (route / conduite / ligne)" },
  { value: "NETWORK", label: "Réseau étendu (distribution / collecte)" },
  { value: "MULTI_BUILDINGS", label: "Ensemble de bâtiments (campus / zone)" },
];

/**
 * Organisation fonctionnelle
 * 
 */

// export const SYSTEMS_ORGANIZATION_OPTIONS = [
//   { value: "INTEGRATED_SYSTEM", label: "Systèmes intégrés (fonctionnement coordonné)" },
//   { value: "INDEPENDENT_SYSTEMS", label: "Systèmes indépendants" },
//   { value: "ZONED_SYSTEM", label: "Système organisé par zones fonctionnelles" },
// ];

export const FUNCTIONAL_ZONES_OPTIONS = [
  { value: "TECH_AREAS", label: "Zones techniques" },
  { value: "OPERATIONS_AREAS", label: "Zones exploitation / maintenance" },
  { value: "PUBLIC_AREAS", label: "Zones usagers / accès public" },
  { value: "LOGISTICS_AREAS", label: "Zones logistique / stockage" },
  { value: "CRITICAL_AREAS", label: "Zones critiques (process / sûreté / HSE)" },
];

export const SUBSYSTEM_DECOMPOSITION_OPTIONS = [
  { value: "MONO_SYSTEM", label: "Un seul système principal" },
  { value: "MULTI_SUBSYSTEMS", label: "Plusieurs sous-systèmes à configurer" },
];




// export const ORGANIZATION_PATTERN_OPTIONS = [
//   { value: "SINGLE_CORE_SITE", label: "Site principal (centre opérationnel)" },
//   { value: "MULTI_SITE_POLES", label: "Plusieurs sites / pôles" },
//   { value: "ZONED_LAYOUT", label: "Organisation par zones (zonage fonctionnel)" },
// ];






/* --- Nature d’intervention (multi-sélection possible) --------------------- */
export const INTERVENTION_NATURE_OPTIONS = [
  { value: "NEW_BUILD", label: "Construction neuve" },
  { value: "EXTENSION", label: "Extension d’un existant" },
  { value: "REHABILITATION", label: "Réhabilitation d’un existant" },
  { value: "DEMOLISH_REBUILD", label: "Démolition incluse" },
];

/* --- Continuité opérationnelle (choix unique) ----------------------------- */
export const OPERATIONAL_CONTINUITY_OPTIONS = [
  { value: "GREENFIELD", label: "Site neuf (sans contraintes d’exploitation)" },
  { value: "BROWNFIELD", label: "Site existant (interfaces avec l’existant)" },
  { value: "LIVE_SITE", label: "Site en exploitation (continuité de service)" },
];

export const PHASED_EXECUTION_OPTION = {
  value: "PHASED", label: "Travaux en phases (bascules / co-activité)",
};









export const FUNCTIONAL_ORGANIZATION_OPTIONS = [
  { value: "CENTRAL_HUB", label: "Pôle central (hub principal)" },
  { value: "MULTI_HUBS", label: "Plusieurs pôles (multi-hubs)" },
  { value: "ZONED_FUNCTIONS", label: "Zonage fonctionnel (zones dédiées)" },
  { value: "TECH_AREAS", label: "Zones techniques (locaux / unités dédiées)" },
  { value: "OPERATIONS_AREAS", label: "Zones exploitation / maintenance" },
  { value: "PUBLIC_AREAS", label: "Zones usagers / accès public" },
  { value: "LOGISTICS_AREAS", label: "Zones logistique (stockage / flux)" },
  { value: "CRITICAL_AREAS", label: "Zones critiques (process / sûreté / HSE)" },
  { value: "PHASED_WORKS", label: "Réalisation par phases (zones / tronçons)" },
  { value: "TEMP_WORKS", label: "Installations provisoires (base vie / déviations)" },
];


/* --- Pilotage (Typology) -------------------------------------------------- */
/**
 * Complexité (V1) : positionnement PM, non technique, déterministe.
 * Usage recommandé : choix unique.
 */
// export const COMPLEXITY_LEVEL_OPTIONS = [
//   { value: "L1", label: "Simple (peu de systèmes et d’interfaces)" },
//   { value: "L2", label: "Standard (plusieurs systèmes interconnectés)" },
//   { value: "L3", label: "Complexe (nombreuses interfaces et dépendances)" },
// ];
export const COMPLEXITY_LEVEL_OPTIONS = [
  { shortLabel: "Simple", longLabel: "Peu de systèmes et d’interfaces" },
  { shortLabel: "Standard", longLabel: "Plusieurs systèmes interconnectés" },
  { shortLabel: "Complexe", longLabel: "Nombreuses interfaces et dépendances" },
];




/**
 * Design maturity : remplace LOW/MEDIUM/HIGH par une lecture praticien.
 * Usage recommandé : choix unique.
 */
// export const DESIGN_MATURITY_OPTIONS = [
//   { value: "LOW", label: "Structuration simple (grandes fonctions du projet)" },
//   { value: "MEDIUM", label: "Structuration détaillée (sous-systèmes principaux)" },
//   { value: "HIGH", label: "Structuration complète (systèmes et sous-systèmes)" },
// ];

export const DESIGN_MATURITY_OPTIONS = [
  { shortLabel: "Bas", longLabel: "Grandes fonctions du projet" },
  { shortLabel: "Moyen", longLabel: "Sous-systèmes principaux" },
  { shortLabel: "Haut", longLabel: "Systèmes et sous-systèmes" },
];









/* --- Contraintes site / Interfaces externes (multi-sélection) ------------- */
export const SITE_CONSTRAINTS_OPTIONS = [
  { value: "URBAN", label: "Contexte urbain" },
  { value: "REMOTE", label: "Site isolé" },
  { value: "HSE", label: "Contraintes HSE" },
  { value: "SECURITY", label: "Contraintes sûreté" },
  { value: "PERMITS", label: "Permis sensibles" },
  { value: "UTILITIES", label: "Réseaux existants" },
];

export const INTERFACES_OPTIONS = [
  { value: "MULTI_CONTRACT", label: "Lots / contrats multiples (interfaces lots)" },
  { value: "THIRD_PARTIES", label: "Tiers (opérateurs, concessions, riverains, autorités)" },
  { value: "OPERATIONS", label: "Exploitation / maintenance (O&M impliqué)" },
  { value: "EXISTING_ASSETS", label: "Ouvrages existants (raccordements / bascules)" },
];

/* --- Référentiels (Typology avancée) ------------------------------------- */
export const STANDARDS_OPTIONS = [
  { value: "ISO_9001", label: "ISO 9001 (qualité)" },
  { value: "ISO_19650", label: "ISO 19650 (management de l’information)" },
  { value: "PMI", label: "PMI (référentiel PM)" },
];



/* NOTE : PROJECT_PHASE_OPTIONS volontairement hors scope (module métier ultérieur)
export const PROJECT_PHASE_OPTIONS = [...];
*/